"use client"

import { useState, useEffect, useRef, useMemo, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import SearchableDropdown from "../searchable-dropdown/SearchableDropdown"
import BrandSubBrandFields from "../brand-sub-brand-fields/BrandSubBrandFields"
import {
  fetchWorldCountries,
  fetchWorldCities,
  formatWorldCountryLabel,
  formatWorldCityLabel,
  resetWorldCities,
  clearWorldGeography,
} from "../../../store/slices/worldGeographySlice"
import {
  searchTimezones,
  formatTimezoneLabel,
  clearTimezones,
  getSystemIanaTimezone,
  TIMEZONE_PAGE_SIZE,
  getTimezoneHasMore,
} from "../../../store/slices/timezonesSlice"
import { searchSocialNetwork } from "../../../store/slices/createAccountModalSlice"
import { inferSocialDescriptionFromHandle } from "../../organization-settings/accountDetailsInit"
import { socialTypeIconPath } from "../../../lib/assets"

const WORLD_GEO_PAGE_SIZE = 100
const WEBSITE_REGEX =
  /^https:\/\/(www\.)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\.[a-zA-Z]{2,})?(\/[a-zA-Z0-9\-._~:/?#[\]@!$&'()*+,;=]*)?$/

const isValidWebsite = (url) => {
  if (!url) return false
  return WEBSITE_REGEX.test(url.trim())
}

const formatSocialLabel = (description = "") => {
  switch ((description || "").toLowerCase()) {
    case "instagram_graph": return "Instagram"
    case "facebook": return "Facebook"
    case "twitter": return "X"
    case "tiktok": return "TikTok"
    default: {
      const d = description || ""
      return d.charAt(0).toUpperCase() + d.slice(1)
    }
  }
}

/** Shared property fields: website, brand, sub-brand, social, country, city, timezone. */
export default function AccountPropertyForm({
  websiteUrl = "",
  brandId = null,
  brandText = "",
  subBrandId = null,
  subBrandText = "",
  socialNetworkTypeId = null,
  socialHandle = "",
  countryId = null,
  countryText = "",
  cityId = null,
  cityText = "",
  timezoneId = null,
  timezoneText = "",
  onChange,
  errors = {},
  showTimezone = false,
  brandRequired = false,
  isEditMode = false,
  idPrefix = "account-property",
  menuPortal = false,
}) {
  const dispatch = useDispatch()
  const socialDropdownRef = useRef(null)
  const autoDetectedRef = useRef(false)

  const worldCountries = useSelector((state) => state.worldGeography.countries)
  const worldCities = useSelector((state) => state.worldGeography.cities)
  const timezoneState = useSelector((state) => state.timezones)

  const [socialList, setSocialList] = useState([])
  const [showSocialDrop, setShowSocialDrop] = useState(false)

  useEffect(() => {
    dispatch(searchSocialNetwork())
      .unwrap()
      .then((res) => setSocialList(res || []))
      .catch(() => {})
  }, [dispatch])

  useEffect(() => {
    dispatch(fetchWorldCountries({ query: "", page: 0, size: WORLD_GEO_PAGE_SIZE, append: false }))
    if (showTimezone) {
      dispatch(searchTimezones({ query: getSystemIanaTimezone() || "", page: 0, size: TIMEZONE_PAGE_SIZE, append: false }))
    }
    return () => {
      dispatch(clearWorldGeography())
      if (showTimezone) dispatch(clearTimezones())
    }
  }, [dispatch, showTimezone])

  useEffect(() => {
    if (!countryId) return
    dispatch(resetWorldCities())
    dispatch(fetchWorldCities({ countryId, query: "", page: 0, size: WORLD_GEO_PAGE_SIZE, append: false }))
  }, [dispatch, countryId])

  useEffect(() => {
    if (!isEditMode || !socialList.length || !socialHandle?.trim()) return
    if (socialNetworkTypeId || autoDetectedRef.current) return
    const desc = inferSocialDescriptionFromHandle(socialHandle)
    if (!desc) return
    const match = socialList.find((item) => String(item.description || "").toLowerCase() === desc.toLowerCase())
    if (match?.id != null) {
      autoDetectedRef.current = true
      onChange?.("socialNetwork", match.id, { ...match, autoDetected: true })
    }
  }, [isEditMode, socialList, socialHandle, socialNetworkTypeId, onChange])

  useEffect(() => {
    const handler = (e) => {
      if (socialDropdownRef.current && !socialDropdownRef.current.contains(e.target)) {
        setShowSocialDrop(false)
      }
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  const countryOptions = useMemo(() => {
    const fromApi = (worldCountries.items || [])
      .map((item) => {
        const label = formatWorldCountryLabel(item)
        return label && item?.id != null ? { label, value: label, id: item.id } : null
      })
      .filter(Boolean)
    if (countryId != null && countryText && !fromApi.some((opt) => String(opt.id) === String(countryId))) {
      return [{ label: countryText, value: countryText, id: countryId }, ...fromApi]
    }
    return fromApi
  }, [worldCountries.items, countryId, countryText])

  const cityOptions = useMemo(() => {
    const fromApi = (worldCities.items || [])
      .map((item) => {
        const label = formatWorldCityLabel(item)
        return label && item?.id != null ? { label, value: label, id: item.id } : null
      })
      .filter(Boolean)
    if (cityId != null && cityText && !fromApi.some((opt) => String(opt.id) === String(cityId))) {
      return [{ label: cityText, value: cityText, id: cityId }, ...fromApi]
    }
    return fromApi
  }, [worldCities.items, cityId, cityText])

  const timezoneOptions = useMemo(() => {
    const fromApi = (timezoneState.items || [])
      .map((item) => {
        const label = formatTimezoneLabel(item)
        return label && item?.id != null ? { label, value: label, id: item.id } : null
      })
      .filter(Boolean)
    if (timezoneId != null && timezoneText && !fromApi.some((opt) => String(opt.id) === String(timezoneId))) {
      return [{ label: timezoneText, value: timezoneText, id: timezoneId }, ...fromApi]
    }
    return fromApi
  }, [timezoneState.items, timezoneId, timezoneText])

  const searchCountries = useCallback(
    (query) => dispatch(fetchWorldCountries({ query: (query || "").trim(), page: 0, size: WORLD_GEO_PAGE_SIZE, append: false })),
    [dispatch],
  )

  const loadMoreCountries = useCallback(() => {
    if (worldCountries.last || worldCountries.loading || worldCountries.loadingMore) return
    dispatch(fetchWorldCountries({
      query: worldCountries.query ?? "",
      page: (worldCountries.page ?? 0) + 1,
      size: WORLD_GEO_PAGE_SIZE,
      append: true,
    }))
  }, [dispatch, worldCountries])

  const searchCities = useCallback(
    (query) => {
      if (!countryId) return
      dispatch(fetchWorldCities({ countryId, query: (query || "").trim(), page: 0, size: WORLD_GEO_PAGE_SIZE, append: false }))
    },
    [dispatch, countryId],
  )

  const loadMoreCities = useCallback(() => {
    if (!countryId || worldCities.last || worldCities.loading || worldCities.loadingMore) return
    dispatch(fetchWorldCities({
      countryId,
      query: worldCities.query ?? "",
      page: (worldCities.page ?? 0) + 1,
      size: WORLD_GEO_PAGE_SIZE,
      append: true,
    }))
  }, [dispatch, countryId, worldCities])

  const searchTimezoneList = useCallback(
    (query) => dispatch(searchTimezones({ query: (query || "").trim(), page: 0, size: TIMEZONE_PAGE_SIZE, append: false })),
    [dispatch],
  )

  const loadMoreTimezones = useCallback(() => {
    if (!getTimezoneHasMore(timezoneState) || timezoneState.loading || timezoneState.loadingMore) return
    dispatch(searchTimezones({
      query: timezoneState.query ?? "",
      page: (timezoneState.page ?? 0) + 1,
      size: TIMEZONE_PAGE_SIZE,
      append: true,
    }))
  }, [dispatch, timezoneState])

  const websiteErrorMsg =
    websiteUrl?.trim() && !isValidWebsite(websiteUrl)
      ? "Enter valid website (e.g., https://www.hotel.com)"
      : ""

  const selectedSocial = socialList.find((item) => item.id === socialNetworkTypeId)

  const handleWebsiteChange = (e) => {
    let value = e.target.value.trimStart()
    if (value && !value.startsWith("http://") && !value.startsWith("https://")) {
      if (value.includes(".")) value = "https://" + value
    }
    onChange?.("websiteUrl", value)
  }

  const handleSocialHandleChange = (e) => {
    const value = e.target.value
    onChange?.("socialHandle", value)
    if (isEditMode && value.trim() && !socialNetworkTypeId && socialList.length) {
      const desc = inferSocialDescriptionFromHandle(value)
      if (desc) {
        const match = socialList.find((item) => String(item.description || "").toLowerCase() === desc.toLowerCase())
        if (match?.id != null) onChange?.("socialNetwork", match.id, { ...match, autoDetected: true })
      }
    }
  }

  return (
    <>
      <label htmlFor={`${idPrefix}-websiteUrl`}>Website URL</label>
      <div className="yourHotels">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
          <path d="M8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M1 8H15M10.5 8C10.5 11.0376 9.433 13.5 8 13.5C6.567 13.5 5.5 11.0376 5.5 8C5.5 4.96243 6.567 2.5 8 2.5C9.433 2.5 10.5 4.96243 10.5 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <input
          id={`${idPrefix}-websiteUrl`}
          type="url"
          placeholder="https://www.yourhotel.com"
          value={websiteUrl}
          onChange={handleWebsiteChange}
          className={(websiteErrorMsg || errors.websiteUrl) ? "input-error" : ""}
          aria-label="Website URL"
        />
      </div>
      {(websiteErrorMsg || errors.websiteUrl) && (
        <p className="error-text">{websiteErrorMsg || errors.websiteUrl}</p>
      )}

      <BrandSubBrandFields
        idPrefix={idPrefix}
        brandId={brandId}
        brandText={brandText}
        subBrandId={subBrandId}
        subBrandText={subBrandText}
        onBrandChange={(text, option) => onChange?.("brand", text, option)}
        onSubBrandChange={(text, option) => onChange?.("subBrand", text, option)}
        brandError={errors.brand || null}
        subBrandError={errors.subBrand || null}
        required={brandRequired}
        menuPortal={menuPortal}
      />

      <label>Social Media Handle</label>
      <p className="social-helper-text">We'll analyze your social profile to tailor insights for your hotel.</p>
      <div className="social-input-row">
        <div className="social-dropdown-wrapper" ref={socialDropdownRef}>
          <div
            className={`social-dropdown-trigger ${socialNetworkTypeId && socialHandle ? "active" : ""}`}
            onClick={(e) => { e.stopPropagation(); setShowSocialDrop((prev) => !prev) }}
          >
            {selectedSocial ? (
              <div className="selected-social">
                <img src={socialTypeIconPath(selectedSocial.description)} alt="" className="social-dropdown-icon" />
                {formatSocialLabel(selectedSocial.description)}
              </div>
            ) : "Select channel"}
            <span className="dropdown-arrow-create">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6.75 13.5L11.25 9L6.75 4.5" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </div>
          {showSocialDrop && (
            <div className="social-dropdown-menu">
              {socialList
                .filter((item) => ["facebook", "instagram_graph", "tiktok"].includes((item.description || "").toLowerCase()))
                .map((item) => (
                  <div
                    key={item.id}
                    className="social-dropdown-item"
                    onClick={() => { onChange?.("socialNetwork", item.id, item); setShowSocialDrop(false) }}
                  >
                    <img src={socialTypeIconPath(item.description)} alt="" className="social-dropdown-icon" />
                    {formatSocialLabel(item.description)}
                  </div>
                ))}
            </div>
          )}
        </div>
        <input
          type="text"
          placeholder={isEditMode ? "@username or profile URL" : socialNetworkTypeId ? "@username" : "Please select a social channel"}
          disabled={!isEditMode && !socialNetworkTypeId}
          value={socialHandle || ""}
          className={!isEditMode && !socialNetworkTypeId ? "input-disabled" : ""}
          onChange={handleSocialHandleChange}
        />
      </div>
      {errors.socialNetwork && <div className="error-text">{errors.socialNetwork}</div>}
      {errors.socialHandle && <div className="error-text">{errors.socialHandle}</div>}

      <SearchableDropdown
        id={`${idPrefix}-country`}
        name="country"
        label="Country"
        value={countryText}
        onChange={(text, option) => onChange?.("country", text, option)}
        onSearch={searchCountries}
        options={countryOptions}
        placeholder="Select Country"
        required
        loading={worldCountries.loading}
        loadingMore={worldCountries.loadingMore}
        hasMore={!worldCountries.last}
        onLoadMore={loadMoreCountries}
        error={errors.country}
        disableEnterSearch
        disableCustomKeyword
        menuPortal={menuPortal}
      />

      <SearchableDropdown
        id={`${idPrefix}-city`}
        name="city"
        label="City/State"
        value={cityText}
        onChange={(text, option) => onChange?.("city", text, option)}
        onSearch={searchCities}
        options={cityOptions}
        placeholder={countryId ? "Search city or state" : "Select a country first"}
        required
        disabled={!countryId}
        loading={worldCities.loading}
        loadingMore={worldCities.loadingMore}
        hasMore={!worldCities.last}
        onLoadMore={loadMoreCities}
        error={errors.city}
        disableEnterSearch
        disableCustomKeyword
        menuPortal={menuPortal}
      />

      {showTimezone && (
        <SearchableDropdown
          id={`${idPrefix}-timezone`}
          name="timezone"
          label="Timezone"
          value={timezoneText}
          onChange={(text, option) => onChange?.("timezone", text, option)}
          onSearch={searchTimezoneList}
          options={timezoneOptions}
          placeholder="Select timezone"
          loading={timezoneState.loading}
          loadingMore={timezoneState.loadingMore}
          hasMore={getTimezoneHasMore(timezoneState)}
          onLoadMore={loadMoreTimezones}
          error={errors.timezone}
          disableEnterSearch
          disableCustomKeyword
          menuPortal={menuPortal}
          menuClassName="searchable-dropdown-popover--compact"
          searchPlaceholder="Search timezone..."
        />
      )}
    </>
  )
}
